import { apiClient as apiClient } from './client';
import {AxiosResponse} from 'axios';
import {Trip} from "@/models/Trip";
import {Offer} from "@/models/Offer";
import {CalendarOffer, SavedOffer} from "@/models/SavedOffer";
import {IAffiliations} from "@/models/Affiliations";
import {CrewMember} from "@/models/CrewMember";
import {Expense} from "@/models/Expense";
import {Boat} from "@/models/Boat";
import {Invoice} from "@/models/Invoice";
import {BaseBudget, Budget} from "@/models/Budget";
import {Refund} from "@/models/Refund";
import {MoneyIn} from "@/models/MoneyIn";

const baseUrl = "/trip";


//Trips
export const getTrips = (): Promise<AxiosResponse<Trip[]>> => {
    return apiClient.get(`${baseUrl}`);
};

export const getTrip = (slug: string): Promise<AxiosResponse<Trip>> => {
    return apiClient.get(`${baseUrl}/${slug}`);
};

export const createTrip = (tripData: Partial<Trip>): Promise<AxiosResponse<Trip>> => {
    return apiClient.post(`${baseUrl}`, tripData);
};

export const updateTrip = (uuid: string, tripData: Partial<Trip>): Promise<AxiosResponse> => {
    return apiClient.put(`${baseUrl}/${uuid}`, tripData);
};


export const deleteTrip = (uuid: string): Promise<AxiosResponse<{ message: string }>> => {
    return apiClient.delete(`${baseUrl}/${uuid}`);
};

export const updateTripImage = (uuid: string, payload: string) => {
    return apiClient.put(`${baseUrl}/${uuid}/image`, payload);
};

//Saved Offers
export const getTripOffers = (uuid: string): Promise<AxiosResponse<Offer[]>> => {
    return apiClient.get(`${baseUrl}/${uuid}/offers`);
};

export const tripOfferAdd = (uuid: string, offer:Offer): Promise<AxiosResponse<Offer>> => {
    return apiClient.put(`${baseUrl}/${uuid}/offers`, offer);
};

export const updateTripOffer = (uuid: string, offerUuid: string,  offer: Partial<Offer>): Promise<AxiosResponse<Offer>> => {
    return apiClient.patch(`${baseUrl}/${uuid}/offers/${offerUuid}`, offer);
};

export const deleteTripOffer = (uuid: string, offerUuid: string): Promise<AxiosResponse<{ message: string }>> => {
    return apiClient.delete(`${baseUrl}/${uuid}/offers/${offerUuid}`);
};

// Get a single saved offer (yacht) from a trip
export const getTripOfferDetail = (uuid: string, yachtUuid: string): Promise<AxiosResponse<Offer>> => {
    return apiClient.get(`${baseUrl}/${uuid}/offers/${yachtUuid}`);
};

// Book (or request) a saved offer for a trip
export const bookTripOffer = (tripUuid: string, offerUuid: string): Promise<AxiosResponse<any>> => {
    return apiClient.post(`${baseUrl}/${tripUuid}/offers/${offerUuid}/book`);
};


//Crew
export const getTripCrew = (uuid: string): Promise<AxiosResponse<CrewMember[]>> => {
    return apiClient.get(`${baseUrl}/${uuid}/crew`);
};
export const addTripCrew = (uuid: string, crewData: Partial<CrewMember>): Promise<AxiosResponse<CrewMember>> => {
    return apiClient.post(`${baseUrl}/${uuid}/crew`, crewData);
};

export const updateTripCrew = (uuid: string, payload: { crewUuids: string[] }): Promise<AxiosResponse<{ message: string }>> => {
    return apiClient.patch(`${baseUrl}/${uuid}/crew`, payload);
};

export const deleteTripCrew = (slug: string, crewUuid: string): Promise<AxiosResponse<{ message: string }>> => {
    return apiClient.delete(`${baseUrl}/${slug}/crew/${crewUuid}`);
};


export const getBilling = (): Promise<AxiosResponse<Invoice[]>> => {
    return apiClient.get(`/billing`);
};

export const getTripInvoices = (uuid: string): Promise<AxiosResponse<Invoice[]>> => {
    return apiClient.get(`${baseUrl}/${uuid}/invoices`);
};

export const getTripInvoice = (uuid: string, invoiceUuid: string): Promise<AxiosResponse<Invoice>> => {
    return apiClient.get(`${baseUrl}/${uuid}/invoices/${invoiceUuid}`);
};

export const createTripInvoice = (uuid: string, invoiceData: Partial<Invoice>): Promise<AxiosResponse<Invoice>> => {
    return apiClient.post(`${baseUrl}/${uuid}/invoices`, invoiceData);
};

export const updateTripInvoice = (uuid: string, invoiceUuid: string, invoiceData: Partial<Invoice>): Promise<AxiosResponse<Invoice>> => {
    return apiClient.patch(`${baseUrl}/${uuid}/invoices/${invoiceUuid}`, invoiceData);
};

export const deleteTripInvoice = (uuid: string, invoiceUuid: string): Promise<AxiosResponse<{
    message: string
}>> => {
    return apiClient.delete(`${baseUrl}/${uuid}/invoices/${invoiceUuid}`);
};

export const getCalendarOffers = (uuid: string): Promise<AxiosResponse<{
    offers: CalendarOffer[],
    boats: Boat[]
}>> => {
    return apiClient.get(`${baseUrl}/${uuid}/saved-offers/calendar`);
}


export const getAffiliations = (): Promise<AxiosResponse<string, IAffiliations[]>> => {
    return apiClient.get("/partner/affiliates");
}


export const getTripCurrentBalance = (uuid: string): Promise<AxiosResponse<BaseBudget>> => {
    return apiClient.get(`${baseUrl}/${uuid}/budget/current-balance`);
};

export const getTripExpenses = (uuid: string): Promise<AxiosResponse<{ expenses: Expense[] }>> => {
    return apiClient.get(`${baseUrl}/${uuid}/budget/expenses`);
};

export const getTripExpenseDetail = (uuid: string, expenseUuid: string): Promise<AxiosResponse<Expense>> => {
    return apiClient.get(`${baseUrl}/${uuid}/budget/expenses/${expenseUuid}`);
};

export const createTripExpense = (uuid: string, expenseData: Partial<Expense>): Promise<AxiosResponse<Expense>> => {
    return apiClient.post(`${baseUrl}/${uuid}/budget/expenses`, expenseData);
};

export const updateTripExpense = (uuid: string, expenseUuid: string, expenseData: Partial<Expense>): Promise<AxiosResponse<Expense>> => {
    return apiClient.patch(`${baseUrl}/${uuid}/budget/expenses/${expenseUuid}`, expenseData);
};

export const deleteTripExpense = (uuid: string, expenseUuid: string): Promise<AxiosResponse<{
    message: string
}>> => {
    return apiClient.delete(`${baseUrl}/${uuid}/budget/expenses/${expenseUuid}`);
};

export const getTripRefunds = (uuid: string): Promise<AxiosResponse<{ refunds: Refund[] }>> => {
    return apiClient.get(`${baseUrl}/${uuid}/budget/refunds`);
};

export const getTripBudgetMoneyIn = (uuid: string): Promise<AxiosResponse<{ moneyIn: MoneyIn[] }>> => {
    return apiClient.get(`${baseUrl}/${uuid}/budget/money-in`);
};

export const getTripBudgetMoneyInDetail = (uuid: string, budgetUuid: string): Promise<AxiosResponse<Budget>> => {
    return apiClient.get(`${baseUrl}/${uuid}/budget/money-in/${budgetUuid}`);
};

export const createTripBudgetMoneyIn = (uuid: string, budgetData: Partial<MoneyIn>): Promise<AxiosResponse<MoneyIn>> => {
    return apiClient.post(`${baseUrl}/${uuid}/budget/money-in`, budgetData);
};

export const updateTripBudgetMoneyIn = (uuid: string, budgetUuid: string, budgetData: Partial<Budget>): Promise<AxiosResponse<Budget>> => {
    return apiClient.patch(`${baseUrl}/${uuid}/budget/money-in/${budgetUuid}`, budgetData);
};

export const deleteTripBudgetMoneyIn = (uuid: string, budgetUuid: string): Promise<AxiosResponse<{
    message: string
}>> => {
    return apiClient.delete(`${baseUrl}/${uuid}/budget/money-in/${budgetUuid}`);
};


export const createInquiry = (id:number, offer: Offer): Promise<AxiosResponse<SavedOffer[]>> => {
    return apiClient.post(`/reservation/inquiry`, offer);
};